import React, { useEffect } from "react";

import { makeStyles, Container, Box, Typography, TextField } from "@material-ui/core";

import { useSelector, useDispatch } from "react-redux";
import { history, RootState } from "../../app/rootReducer";
import { fetchQuotes, setQueryParams } from "../../store/QuotesSlice";
import { colors } from "swyft-react-core";
import { SwyftTable, ColDef } from "../../components/SwyftTable";
import { EQuoteOrderableField } from "../../api/Quote/EQuoteOrderableField";
import { DebounceInput } from "react-debounce-input";
import { RouteComponentProps, useLocation } from "react-router";

const useStyles = makeStyles((theme) => ({
  root: {},
  tableHead: {
    "& th": {
      fontWeight: "bold"
    }
  },
  tableRow: {
    "& tr:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    },
    "& tr:hover": {
      backgroundColor: colors.alternate
    }
  },
  hideInXs: {
    [theme.breakpoints.down("xs")]: {
      display: "none"
    }
  },
  actionIconButton: {
    padding: 0,
    marginRight: "-5px"
  },
  actionIcon: {
    height: "2.5rem",
    width: "2.5rem"
  },
  search: {
    fontSize: "1rem",
    padding: theme.spacing(1),
    width: "14rem"
  }
}));

type Props = RouteComponentProps;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const QuotesPage: React.FC<Props> = (route: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  let query = useQuery().get("query");

  const { isLoading, error, quotesById, currentPageQuotes, queryParams } = useSelector(
    (state: RootState) => state.quotes
  );

  useEffect(() => {
    if (query && queryParams.searchQuery === undefined) {
      dispatch(setQueryParams({ ...queryParams, searchQuery: query }));
    } else {
      dispatch(fetchQuotes(queryParams));
    }
  }, [dispatch, query, queryParams]);

  if (error) {
    console.log(error);
    return <div>Error</div>;
  }

  const columns: ColDef[] = [
    { field: "id", width: 150, hidden: true },
    {
      field: "name",
      headerName: "Name",
      width: 150
    },
    { field: "location", headerName: "Location", width: 150 },
    {
      field: "quote",
      headerName: "Quote",
      sortName: EQuoteOrderableField.Name,
      width: 150
    },
    {
      field: "assessor",
      headerName: "Assessor",
      width: 150
    },
    {
      field: "type",
      headerName: "Type",
      width: 150
    },
    {
      field: "status",
      headerName: "Status",
      width: 150
    },
    {
      field: "date",
      headerName: "Date",
      sortName: EQuoteOrderableField.Date,
      width: 200,
      type: "dateTime"
    }
  ];

  const handleRowClick = (rowId: string | number) => {
    history.push(`/quote/${rowId}`);
  };

  const handleSearchChange = (searchQuery: string) => {
    dispatch(setQueryParams({ ...queryParams, searchQuery, page: 1 }));
  };

  const handleSort = (fieldName: string) => {
    var field = fieldName as EQuoteOrderableField;
    var ascending = field === queryParams.order ? !queryParams.ascending : true;
    dispatch(setQueryParams({ ...queryParams, order: field, ascending }));
  };

  const handlePageChange = (page: number) => {
    dispatch(setQueryParams({ ...queryParams, page }));
  };

  const quotes = currentPageQuotes.map((id) => quotesById[id]);
  const rows = Object.values(quotes).map((q) => {
    return {
      id: q.quoteId,
      name: q.customerName,
      location: q.location,
      quote: q.name,
      assessor: q.assessorName,
      type: q.type,
      status: q.status,
      date: q.created
    };
  });

  return (
    <Container maxWidth="lg">
      <Box className={classes.root} my={2}>
        <Box px={2} py={2} display="flex">
          <Box flexGrow={1}>
            <Typography variant="h2" paragraph>
              Pending Quotes
            </Typography>
          </Box>
          <DebounceInput
            component={TextField}
            variant="outlined"
            size="small"
            minLength={2}
            debounceTimeout={300}
            value={queryParams.searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by Swyft Id or Name"
            className={classes.search}
          />
        </Box>
        <SwyftTable
          isLoading={isLoading}
          rows={rows}
          columns={columns}
          handleRowClick={(rowId) => handleRowClick(rowId)}
          sort={queryParams.order}
          sortAscending={false}
          handleSort={(fieldName) => handleSort(fieldName)}
          page={queryParams.page}
          pageCount={0}
          handlePageChange={(p) => handlePageChange(p)}
        />
      </Box>
    </Container>
  );
};

export default QuotesPage;
